/* eslint-disable react/jsx-props-no-spreading */
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Column } from 'app/components/ui/column';
import { DesktopContentBlock } from 'app/components/content-block/destop-content-block';
import { MobileContentBlock } from 'app/components/content-block/mobile-content-block';
import { IconBlockInfo } from 'app/components/ui/icon-block';
import { sanitize } from 'app/shared/utils/sanitize-html';
import { useSanitizedTagsQuery } from 'app/data/queries/use-sanitized-tags-query';
// import { useFeatureFlags } from 'app/shared/contexts/feature-flags/feature-flags.context';
import { formatEventDate } from 'app/shared/utils/format-event-date';
import { formatEventDateRange } from 'app/shared/utils/format-event-date-range';
import { formatEventDuration } from 'app/shared/utils/format-event-duration';
import { formatGoogleMapLinkForAddress } from 'app/shared/utils/format-google-map-link';
import { formatGoogleCalendarLink } from 'app/shared/utils/format-google-calendar-link';
import { Attachable } from 'app/components/attachable';
import { TenantContentData } from 'app/data/types/content';
import { useSdkHostInfo } from 'app/shared/hooks/use-sdk-host-info';
import { HOST_PLATFORMS } from 'app/shared/consts';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk/miniapp-sdk.context';
import { useNavigate } from 'react-router-dom';
import { useOpenParams } from 'app/shared/hooks/use-open-params';
import { Building } from 'app/data/types/building';

interface ContentViewProps {
  content: TenantContentData;
  building: Building | null;
  isMobile: boolean;
}

export const ContentView = ({ content, building, isMobile }: ContentViewProps): JSX.Element => {
  const intl = useIntl();
  // VIS-1589: a missing `building.timezone` is a data problem to surface, not to paper over with
  // US Eastern — `America/New_York` skews UK/EU buildings 5–6h, so an event renders at the wrong
  // wall-clock time. Fall back to UTC (the single agreed cross-service behaviour across helix,
  // resource-booking-service, and these workers). `||` (not `??`) so an empty-string timezone also
  // falls back. Resolution stays pure; the warning fires from a committed effect below (a
  // `console.warn` in render re-runs under StrictMode / discarded renders).
  const buildingTimezone = useMemo(() => building?.timezone || 'UTC', [building?.timezone]);

  useEffect(() => {
    if (!building?.timezone) {
      // eslint-disable-next-line no-console
      console.warn('[content-view] building.timezone is missing; falling back to UTC (VIS-1589)', {
        buildingUuid: building?.uuid,
        contentUuid: content?.uuid,
      });
    }
  }, [building?.timezone, building?.uuid, content?.uuid]);
  const hostedBy = content?.host_company_name ?? content?.host_custom_text;
  const categoryName = content?.targeting?.category_name;
  const { data: tags } = useSanitizedTagsQuery();
  const eventStartDate = formatEventDate((content?.start_at as unknown) as string, intl.locale, buildingTimezone);
  const eventEndDate = formatEventDate((content?.end_at as unknown) as string, intl.locale, buildingTimezone);
  const dateTime = formatEventDateRange(eventStartDate, eventEndDate);
  const { initialUrl } = useOpenParams();
  const client = useMiniappSdk();
  const navigate = useNavigate();

  const { hostPlatform } = useSdkHostInfo();
  const hideBackTo = hostPlatform !== 'web';
  // The SDK's host platform is only reliably populated for the two web hosts — `web` (tenant web) and
  // `tenant-portal`. For native (iOS/Android) it often returns nothing, so we DON'T detect mobile
  // positively: the miniapp is treated as a mobile host unless the platform is explicitly one of the
  // web hosts (an unresolved/undefined platform therefore reads as mobile). On a mobile host the
  // miniapp owns its back button, so wire it into the desktop block's header — otherwise it vanishes
  // when a phone in landscape crosses the max-width query into the desktop layout (VIS-1542). Web keeps
  // its own "< Back" button (this header isn't rendered there); tenant-portal keeps its share-only
  // header and self-manages via its own breakpoints.
  const isMobileHost = hostPlatform !== HOST_PLATFORMS.WEB
    && hostPlatform !== HOST_PLATFORMS.TENANT_PORTAL;

  const iconBlocks = useMemo(() => {
    const blocks = [];
  if (content?.start_at && content?.end_at) {
    blocks.push({
      icon: 'calendar-alt',
      title: dateTime,
      subtitle: formatEventDuration(
        (content.start_at as unknown) as string,
        (content.end_at as unknown) as string,
        intl.locale,
        buildingTimezone,
      ),
      linkText: intl.formatMessage({ id: 'add_to_calendar' }),
      linkRef: formatGoogleCalendarLink(
        content.title ?? '',
        buildingTimezone,
        (content.start_at as unknown) as string,
        (content.end_at as unknown) as string,
      ),
      titleType: 'bold',
      shouldOpenInNewTab: true,
    });
  }
  if (content?.start_at && !content?.end_at) {
    blocks.push({
      icon: 'calendar-alt',
      title: eventStartDate.fullDate,
      // The guarded util's value, not a second raw formatLocaleTime call. formatEventDate already
      // computed exactly this from the same locale and zone, and returns '' for an unusable date —
      // whereas formatLocaleTime throws RangeError, which took the view down when start_at was
      // truthy but unparseable and there was no end_at to route around this branch.
      subtitle: eventStartDate.eventStartTime,
      linkText: intl.formatMessage({ id: 'add_to_calendar' }),
      linkRef: formatGoogleCalendarLink(
        content.title ?? '',
        buildingTimezone,
        (content.start_at as unknown) as string,
      ),
      titleType: 'bold',
      shouldOpenInNewTab: true,
    });
  }
  if (content?.formatted_address) {
    blocks.push({
      icon: 'map-marker-alt',
      title: content.formatted_address,
      titleType: 'bold',
      linkText: intl.formatMessage({ id: 'open_in_maps' }),
      subtitle: content?.directions ?? '',
      linkRef: formatGoogleMapLinkForAddress(content.formatted_address),
      shouldOpenInNewTab: true,
    });
  }
  if (content?.virtual_text && content?.virtual_url) {
    blocks.push({
      icon: 'link',
      title: intl.formatMessage({ id: 'virtual' }),
      titleType: 'bold',
      linkText: content.virtual_text,
      subtitle: content?.virtual_directions ?? '',
      linkRef: content.virtual_url,
      shouldOpenInNewTab: true,
    });
  }
  return blocks;
  }, [content, intl, buildingTimezone, dateTime, eventStartDate]);

  const onBackClick = useCallback(() => {
    const closeMiniapp = initialUrl && content?.uuid && initialUrl.includes(content?.uuid);

    if (closeMiniapp) {
      client?.close();
    } else {
      navigate(-1);
    }
  }, [client, navigate, initialUrl, content?.uuid]);

  const contentBlockProps = useMemo(() => ({
    description: content?.description,
    descriptionTitleText: '',
    descriptionViewDetailText: intl.formatMessage({ id: 'view_details' }),
    richDescription: content?.rich_description_type === 'BASIC' ? sanitize(content?.rich_description ?? '', tags?.data as unknown as string[]) : content?.rich_description,
    showRichDescription: true,
    hostedBy: hostedBy && intl.formatMessage({ id: 'hosted_by' }, { name: hostedBy }),
    iconBlocks: iconBlocks as IconBlockInfo[],
    imgUrl: `${'https://hqo.imgix.net/'}${content?.image_url ?? ''}`,
    imgAltText: content?.image_alt_text ?? '',
    lowerSubtitle: content?.subtitle ?? '',
    title: content?.title ?? '',
    upperSubtitle: categoryName ?? '',
    onBackClick,
  }), [content, tags, intl, hostedBy, categoryName, iconBlocks, onBackClick]);

  const getContentBlock = useCallback(() => {
    if (isMobile) {
      return <MobileContentBlock {...contentBlockProps} />;
    }
    return <DesktopContentBlock {...contentBlockProps} hideBackTo={hideBackTo} isMobileHost={isMobileHost} />;
  }, [isMobile, contentBlockProps, hideBackTo, isMobileHost]);

  return (
    <Fragment>
      <Column>
        {getContentBlock()}
      </Column>
      {content?.cta_type && <Attachable attachable={content} isMobile={isMobile} />}
    </Fragment>
  );
};
